import { useCallback, useEffect, useMemo, useState } from 'react'
import type { BuxusOperatorPayload, HomeMapMode } from '@buxus/shared'
import { operatorMapLegendItemsFromRecord } from '@buxus/shared'
import { Map as MapView } from './Map'
import { MapSurface } from './MapSurface'
import { AppLoadingRow } from './components/AppBootLoading'
import type { PipelineMapOperatorDirectoryEntry } from './mapPipelinePopupDom'
import type { PersistedOperatorEntry } from './platform/buxus/OperatorSessionContext'
import { useMapRailHoverPreview } from './platform/ui/useMapRailHoverPreview'
import { MapOperatorOverlayRail } from './components/operators/MapOperatorOverlayRail'
import { OperatorMapPreviewCard } from './components/operators/OperatorMapPreviewCard'
import {
  buildHomeOperatorCatalogEntry,
  buildOperatorMapPreviewSnapshot,
} from './components/operators/operatorMapPreview'
import { isMobileWebViewport, useMobileWebViewport } from './platform/ui/mobileViewport'
import type { ScreenInteractionContext } from './platform/analytics/interactionAnalytics'

type HomeOperatorsMapScreenProps = {
  operators: readonly PersistedOperatorEntry[]
  mapMode: HomeMapMode
  stakeholderCode: string | null
  getOperatorPayload: (operatorId: string) => BuxusOperatorPayload | null
  ensureOperatorPayload: (
    operatorId: string,
    stakeholderCodeForView: string | null,
  ) => Promise<BuxusOperatorPayload>
  jurisdictionCountyStateKeys: ReadonlySet<string> | null
  pipelineOperatorDirectory: readonly PipelineMapOperatorDirectoryEntry[]
  onViewPipelineOperator: (operatorId: string) => void
  analytics?: ScreenInteractionContext
}

export function HomeOperatorsMapScreen({
  operators,
  mapMode,
  stakeholderCode,
  getOperatorPayload,
  ensureOperatorPayload,
  jurisdictionCountyStateKeys,
  pipelineOperatorDirectory,
  onViewPipelineOperator,
  analytics,
}: HomeOperatorsMapScreenProps) {
  const [railOpen, setRailOpen] = useState(() => !isMobileWebViewport())
  const isMobileWeb = useMobileWebViewport()
  const [pipelineOverlayActive, setPipelineOverlayActive] = useState(false)
  const [scopedPayload, setScopedPayload] = useState<BuxusOperatorPayload | null>(null)
  const [scopedPayloadLoading, setScopedPayloadLoading] = useState(false)
  const [scopedPayloadError, setScopedPayloadError] = useState<string | null>(null)

  const {
    hoverId,
    previewId,
    showPreview,
    onHoverStart,
    onHoverEnd,
    onPreviewCardEnter,
    onPreviewCardLeave,
    clearPreview,
  } = useMapRailHoverPreview({ overlayBlocked: pipelineOverlayActive })

  const catalog = useMemo(() => {
    const entries = operators.map((op) =>
      buildHomeOperatorCatalogEntry(
        op,
        operatorMapLegendItemsFromRecord(getOperatorPayload(op.operatorId)?.operator),
      ),
    )
    return [...entries].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    )
  }, [operators, getOperatorPayload])

  const scopedOperatorId = mapMode === 'operatorScoped' ? (catalog[0]?.operatorId ?? null) : null

  const operatorsById = useMemo(
    () => new Map(operators.map((op) => [op.operatorId, op])),
    [operators],
  )

  const previewOperator = previewId ? (operatorsById.get(previewId) ?? null) : null
  const previewSnapshot = useMemo(() => {
    if (!previewOperator) return null
    return buildOperatorMapPreviewSnapshot(
      previewOperator,
      getOperatorPayload(previewOperator.operatorId),
    )
  }, [previewOperator, getOperatorPayload])

  useEffect(() => {
    if (mapMode !== 'operatorScoped' || !scopedOperatorId) {
      setScopedPayload(null)
      setScopedPayloadLoading(false)
      setScopedPayloadError(null)
      return
    }

    const cached = getOperatorPayload(scopedOperatorId)
    if (cached) {
      setScopedPayload(cached)
      setScopedPayloadError(null)
    }

    let cancelled = false
    setScopedPayloadLoading(!cached)
    void (async () => {
      try {
        const payload = await ensureOperatorPayload(scopedOperatorId, stakeholderCode)
        if (cancelled) return
        setScopedPayload(payload)
        setScopedPayloadError(null)
      } catch (e: unknown) {
        if (cancelled) return
        if (!cached) setScopedPayload(null)
        setScopedPayloadError(e instanceof Error ? e.message : String(e))
      } finally {
        if (!cancelled) setScopedPayloadLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    mapMode,
    scopedOperatorId,
    stakeholderCode,
    getOperatorPayload,
    ensureOperatorPayload,
  ])

  const resetMapSelection = useCallback(() => {
    clearPreview()
  }, [clearPreview])

  const onFocusOperator = useCallback(
    (entry: (typeof catalog)[number]) => {
      clearPreview()
      onViewPipelineOperator(entry.operatorId)
    },
    [clearPreview, onViewPipelineOperator],
  )

  const onRailOpenChange = useCallback(
    (open: boolean) => {
      setRailOpen(open)
      if (!open) resetMapSelection()
    },
    [resetMapSelection],
  )

  useEffect(() => {
    const onKeyDown = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') resetMapSelection()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [resetMapSelection])

  const mapReady =
    mapMode === 'explorer' || (!!scopedPayload && !scopedPayloadLoading && !scopedPayloadError)

  return (
    <MapSurface variant="fluid" className="home-operators-map-surface">
      <div className="home-operators-map-host" data-map-rail-open={railOpen ? 'true' : undefined}>
        {mapMode === 'operatorScoped' && scopedPayloadLoading && !scopedPayload ? (
          <div className="main-session-loading main-session-loading--inset">
            <AppLoadingRow />
          </div>
        ) : null}
        {mapMode === 'operatorScoped' && scopedPayloadError && !scopedPayload ? (
          <p className="app-download-empty home-operators-map-load-error">{scopedPayloadError}</p>
        ) : null}
        {mapReady ? (
          <MapView
            key={
              mapMode === 'explorer'
                ? 'map-explorer-home'
                : `map-home-scoped-${scopedOperatorId ?? 'none'}`
            }
            mapRole={mapMode === 'explorer' ? 'explorerHome' : 'embeddedOperator'}
            payload={mapMode === 'operatorScoped' ? (scopedPayload ?? undefined) : undefined}
            stakeholderCode={mapMode === 'operatorScoped' ? stakeholderCode : null}
            embedded
            jurisdictionCountyStateKeys={jurisdictionCountyStateKeys}
            pipelineOperatorDirectory={pipelineOperatorDirectory}
            onViewPipelineOperator={onViewPipelineOperator}
            onMapBackgroundClick={resetMapSelection}
            onPipelineOverlayActiveChange={setPipelineOverlayActive}
            analytics={analytics}
          />
        ) : null}
        <MapOperatorOverlayRail
          entries={catalog}
          totalCount={catalog.length}
          open={railOpen}
          selectedOperatorId={null}
          hoverOperatorId={hoverId}
          onOpenChange={onRailOpenChange}
          onPreviewHoverStart={onHoverStart}
          onPreviewHoverEnd={onHoverEnd}
          onFocusOperator={onFocusOperator}
          analytics={analytics}
        />
        {showPreview && previewSnapshot && !isMobileWeb ? (
          <OperatorMapPreviewCard
            snapshot={previewSnapshot}
            onMouseEnter={onPreviewCardEnter}
            onMouseLeave={onPreviewCardLeave}
          />
        ) : null}
      </div>
    </MapSurface>
  )
}
